import settlementService, {
  PayableItem,
  PayableSummary,
} from "@/services/settlement";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  NumberFormatter,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconCalendar,
  IconCash,
  IconCheck,
  IconHistory,
  IconInfoCircle,
  IconReceipt,
  IconRefresh,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "react-multi-date-picker/styles/layouts/mobile.css";
import { useMemo, useState } from "react";

const today = dayjs().format("YYYY-MM-DD");
const monthStart = dayjs().startOf("month").format("YYYY-MM-DD");
const toGregorianDate = (value: DateObject | null) =>
  value ? dayjs(value.toDate()).format("YYYY-MM-DD") : "";
const money = (value: number) => (
  <NumberFormatter value={value || 0} thousandSeparator="," suffix=" ریال" />
);
const displayDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

function EmployeeSettlements() {
  const queryClient = useQueryClient();
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [dateRange, setDateRange] = useState<DateObject[]>([
    new DateObject(new Date(`${monthStart}T00:00:00`)),
    new DateObject(new Date(`${today}T00:00:00`)),
  ]);
  const [selectedEmployee, setSelectedEmployee] = useState<PayableSummary>();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [settleDate, setSettleDate] = useState(today);
  const [rrn, setRrn] = useState("");
  const [description, setDescription] = useState("");
  const [modalOpened, modal] = useDisclosure(false);
  const [cancelModalOpened, cancelModal] = useDisclosure(false);
  const [detailModalOpened, detailModal] = useDisclosure(false);
  const [cancelSettlementId, setCancelSettlementId] = useState<number>();
  const [detailSettlementId, setDetailSettlementId] = useState<number>();
  const [cancelReason, setCancelReason] = useState("");

  const summaryQuery = useQuery({
    queryKey: ["settlement-payable-summary", fromDate, toDate],
    queryFn: () => settlementService.getPayableSummary(fromDate, toDate),
  });
  const itemsQuery = useQuery({
    queryKey: [
      "settlement-payable-items",
      selectedEmployee?.merchantUserId,
      fromDate,
      toDate,
    ],
    queryFn: () =>
      settlementService.getPayableItems({
        merchantUserId: selectedEmployee!.merchantUserId,
        fromDate,
        toDate,
        skip: 0,
        count: 200,
      }),
    enabled: Boolean(selectedEmployee),
  });
  const historyQuery = useQuery({
    queryKey: ["settlement-history"],
    queryFn: () => settlementService.getHistory({ skip: 0, count: 100 }),
  });
  const detailQuery = useQuery({
    queryKey: ["settlement-detail", detailSettlementId],
    queryFn: () => settlementService.getDetail(detailSettlementId!),
    enabled: Boolean(detailSettlementId && detailModalOpened),
  });

  const selectedAmount = useMemo(
    () =>
      (itemsQuery.data?.result || [])
        .filter((item) => selectedItems.includes(item.customerPurchaseItemId))
        .reduce((sum, item) => sum + item.amount, 0),
    [itemsQuery.data, selectedItems]
  );
  const totalDebt = (summaryQuery.data || []).reduce(
    (sum, employee) => sum + employee.totalAmount,
    0
  );

  const createMutation = useMutation({
    mutationFn: () =>
      settlementService.create({
        merchantUserId: selectedEmployee!.merchantUserId,
        fromDate,
        toDate,
        customerPurchaseItemIds: selectedItems,
        settleDate,
        rrn: rrn || undefined,
        description: description || undefined,
      }),
    onSuccess: async () => {
      notifications.show({
        color: "green",
        icon: <IconCheck size={18} />,
        message: "تسویه عضو تیم با موفقیت ثبت شد",
      });
      modal.close();
      setSelectedItems([]);
      await Promise.all([
        summaryQuery.refetch(),
        itemsQuery.refetch(),
        historyQuery.refetch(),
      ]);
      // Invoice details show each item as settled or not.
      void queryClient.invalidateQueries({ queryKey: ["invoice-detail"] });
    },
  });
  const cancelMutation = useMutation({
    mutationFn: settlementService.cancel,
    onSuccess: async () => {
      notifications.show({ color: "green", message: "سند تسویه ابطال شد" });
      cancelModal.close();
      setCancelReason("");
      setCancelSettlementId(undefined);
      // The cancelled settlement's items become payable again.
      await Promise.all([historyQuery.refetch(), summaryQuery.refetch(), selectedEmployee ? itemsQuery.refetch() : undefined]);
      void queryClient.invalidateQueries({ queryKey: ["settlement-detail"] });
      void queryClient.invalidateQueries({ queryKey: ["invoice-detail"] });
    },
  });

  const selectEmployee = (employee: PayableSummary) => {
    setSelectedEmployee(employee);
    setSelectedItems([]);
  };
  const toggleItem = (item: PayableItem) =>
    setSelectedItems((current) =>
      current.includes(item.customerPurchaseItemId)
        ? current.filter((id) => id !== item.customerPurchaseItemId)
        : [...current, item.customerPurchaseItemId]
    );
  const allItemIds = (itemsQuery.data?.result || []).map(
    (item) => item.customerPurchaseItemId
  );

  return (
    <Box p={{ base: "md", md: "xl" }} dir="rtl" maw={1500} w="100%" mx="auto">
      <LoadingOverlay visible={summaryQuery.isLoading} />
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <Box>
            <Title order={2}>تسویه تیم</Title>
            <Text c="dimmed" mt={6}>
              مشاهده سهم خدمات انجام‌شده و ثبت پرداخت به تیم
            </Text>
          </Box>
          <ActionIcon
            size="lg"
            variant="default"
            aria-label="به‌روزرسانی"
            onClick={() => summaryQuery.refetch()}
          >
            <IconRefresh size={19} />
          </ActionIcon>
        </Group>

        <Paper withBorder radius="lg" p="lg">
          <Group justify="space-between" align="end">
            <DatePicker
              calendar={persian}
              locale={persian_fa}
              className="rmdp-mobile"
              range
              rangeHover
              value={dateRange}
              onChange={(values) => {
                if (!Array.isArray(values)) return;
                setDateRange(values);
                if (values.length === 2) {
                  setFromDate(toGregorianDate(values[0]));
                  setToDate(toGregorianDate(values[1]));
                }
              }}
              render={
                <TextInput
                  label="بازه تاریخ"
                  placeholder="تاریخ شروع و پایان را انتخاب کنید"
                  leftSection={<IconCalendar size={17} />}
                  w={320}
                  readOnly
                />
              }
            />
            <Text size="sm" c="dimmed">
              فقط خریدهای پرداخت‌شده و تسویه‌نشده نمایش داده می‌شوند.
            </Text>
          </Group>
        </Paper>

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Card withBorder radius="lg" p="lg">
            <Group>
              <ThemeIcon size="xl" radius="md" variant="light">
                <IconCash />
              </ThemeIcon>
              <Box>
                <Text size="sm" c="dimmed">جمع بدهی قابل پرداخت</Text>
                <Text fw={800} size="xl">{money(totalDebt)}</Text>
              </Box>
            </Group>
          </Card>
          <Card withBorder radius="lg" p="lg">
            <Group>
              <ThemeIcon size="xl" radius="md" color="teal" variant="light">
                <IconUsers />
              </ThemeIcon>
              <Box>
                <Text size="sm" c="dimmed">طلبکاران تیم</Text>
                <Text fw={800} size="xl">{summaryQuery.data?.length || 0} نفر</Text>
              </Box>
            </Group>
          </Card>
          <Card withBorder radius="lg" p="lg">
            <Group>
              <ThemeIcon size="xl" radius="md" color="grape" variant="light">
                <IconReceipt />
              </ThemeIcon>
              <Box>
                <Text size="sm" c="dimmed">آیتم‌های تسویه‌نشده</Text>
                <Text fw={800} size="xl">
                  {(summaryQuery.data || []).reduce((sum, item) => sum + item.itemCount, 0)} مورد
                </Text>
              </Box>
            </Group>
          </Card>
        </SimpleGrid>

        <Tabs defaultValue="payable" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="payable" leftSection={<IconCash size={17} />}>
              قابل تسویه
            </Tabs.Tab>
            <Tabs.Tab value="history" leftSection={<IconHistory size={17} />}>
              تاریخچه تسویه‌ها
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="payable" pt="lg">
            <Stack gap="lg">
              <Paper withBorder radius="lg" style={{ overflow: "hidden" }}>
                <ScrollArea>
                  <Table striped highlightOnHover verticalSpacing="md" miw={720}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>عضو تیم</Table.Th>
                        <Table.Th>تعداد خدمات</Table.Th>
                        <Table.Th>مبلغ قابل پرداخت</Table.Th>
                        <Table.Th>عملیات</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {(summaryQuery.data || []).map((employee) => (
                        <Table.Tr key={employee.merchantUserId}>
                          <Table.Td fw={600}>
                            {employee.merchantUserName || `عضو تیم شماره ${employee.merchantUserId}`}
                          </Table.Td>
                          <Table.Td>{employee.itemCount}</Table.Td>
                          <Table.Td fw={700}>{money(employee.totalAmount)}</Table.Td>
                          <Table.Td>
                            <Button
                              size="xs"
                              variant={selectedEmployee?.merchantUserId === employee.merchantUserId ? "filled" : "light"}
                              onClick={() => selectEmployee(employee)}
                            >
                              مشاهده جزئیات
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                      {!summaryQuery.isLoading && !summaryQuery.data?.length && (
                        <Table.Tr><Table.Td colSpan={4} ta="center" py="xl">بدهی تسویه‌نشده‌ای در این بازه وجود ندارد.</Table.Td></Table.Tr>
                      )}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </Paper>

              {selectedEmployee && (
                <Paper withBorder radius="lg" p="lg" pos="relative">
                  <LoadingOverlay visible={itemsQuery.isLoading} />
                  <Group justify="space-between" mb="md">
                    <Box>
                      <Text fw={800} size="lg">
                        جزئیات {selectedEmployee.merchantUserName || `عضو تیم شماره ${selectedEmployee.merchantUserId}`}
                      </Text>
                      <Text size="sm" c="dimmed">آیتم‌هایی را که پرداخت شده‌اند انتخاب کنید.</Text>
                    </Box>
                    <Button disabled={!selectedItems.length} onClick={modal.open}>
                      ثبت تسویه ({selectedItems.length})
                    </Button>
                  </Group>
                  <Divider mb="md" />
                  <ScrollArea>
                    <Table verticalSpacing="sm" miw={900}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th w={42}>
                            <Checkbox
                              checked={Boolean(allItemIds.length) && selectedItems.length === allItemIds.length}
                              indeterminate={selectedItems.length > 0 && selectedItems.length < allItemIds.length}
                              onChange={() => setSelectedItems(selectedItems.length === allItemIds.length ? [] : allItemIds)}
                            />
                          </Table.Th>
                          <Table.Th>تاریخ خرید</Table.Th><Table.Th>مشتری</Table.Th><Table.Th>خدمت</Table.Th>
                          <Table.Th>مبلغ مشتری</Table.Th><Table.Th>کارمزد مجموعه</Table.Th><Table.Th>سهم تیم</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {(itemsQuery.data?.result || []).map((item) => (
                          <Table.Tr key={item.customerPurchaseItemId}>
                            <Table.Td><Checkbox checked={selectedItems.includes(item.customerPurchaseItemId)} onChange={() => toggleItem(item)} /></Table.Td>
                            <Table.Td>{displayDate(item.purchaseDate)}</Table.Td>
                            <Table.Td>{item.customerName || "—"}</Table.Td>
                            <Table.Td>{item.serviceName || "—"}</Table.Td>
                            <Table.Td>{money(item.grossAmount)}</Table.Td>
                            <Table.Td c="dimmed">{money(item.commissionAmount)}</Table.Td>
                            <Table.Td fw={700} c="teal.8">{money(item.amount)}</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>
                  {selectedItems.length > 0 && (
                    <Group justify="flex-end" mt="lg">
                      <Text c="dimmed">جمع انتخاب‌شده:</Text>
                      <Text fw={800} size="lg">{money(selectedAmount)}</Text>
                    </Group>
                  )}
                </Paper>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="history" pt="lg">
            <Paper withBorder radius="lg" style={{ overflow: "hidden" }} pos="relative">
              <LoadingOverlay visible={historyQuery.isLoading} />
              <ScrollArea>
                <Table striped highlightOnHover verticalSpacing="md" miw={900}>
                  <Table.Thead><Table.Tr><Table.Th>شماره سند</Table.Th><Table.Th>عضو تیم</Table.Th><Table.Th>تاریخ پرداخت</Table.Th><Table.Th>تعداد</Table.Th><Table.Th>مبلغ</Table.Th><Table.Th>وضعیت</Table.Th><Table.Th>عملیات</Table.Th></Table.Tr></Table.Thead>
                  <Table.Tbody>
                    {(historyQuery.data?.result || []).map((record) => (
                      <Table.Tr key={record.id}>
                        <Table.Td>#{record.id}</Table.Td><Table.Td>{record.merchantUserName || record.merchantUserId}</Table.Td><Table.Td>{displayDate(record.settleDate)}</Table.Td><Table.Td>{record.itemCount}</Table.Td><Table.Td fw={700}>{money(record.totalAmount)}</Table.Td>
                        <Table.Td><Badge color={record.status === 0 ? "green" : "gray"}>{record.status === 0 ? "تأییدشده" : "باطل‌شده"}</Badge></Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <Button
                              variant="subtle"
                              size="xs"
                              onClick={() => {
                                setDetailSettlementId(record.id);
                                detailModal.open();
                              }}
                            >
                              جزئیات
                            </Button>
                            {record.status === 0 && (
                              <Button
                                color="red"
                                variant="subtle"
                                size="xs"
                                leftSection={<IconX size={15} />}
                                onClick={() => {
                                  setCancelSettlementId(record.id);
                                  cancelModal.open();
                                }}
                              >
                                ابطال
                              </Button>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Paper>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <Modal opened={modalOpened} onClose={modal.close} title="تأیید و ثبت تسویه" centered size="lg">
        <Stack>
          <Alert icon={<IconInfoCircle size={18} />} color="blue" variant="light">
            این عملیات فقط ثبت حسابداری پرداخت است و انتقال بانکی انجام نمی‌دهد.
          </Alert>
          <SimpleGrid cols={2}>
            <Box><Text size="sm" c="dimmed">تعداد آیتم</Text><Text fw={700}>{selectedItems.length} مورد</Text></Box>
            <Box><Text size="sm" c="dimmed">مبلغ پرداخت</Text><Text fw={800} c="teal.8">{money(selectedAmount)}</Text></Box>
          </SimpleGrid>
          <DatePicker
            calendar={persian}
            locale={persian_fa}
            className="rmdp-mobile"
            value={new Date(`${settleDate}T00:00:00`)}
            onChange={(value) => setSettleDate(toGregorianDate(value))}
            render={
              <TextInput
                label="تاریخ پرداخت"
                placeholder="تاریخ پرداخت را انتخاب کنید"
                leftSection={<IconCalendar size={17} />}
                required
                readOnly
              />
            }
          />
          <TextInput label="شماره پیگیری بانکی" description="اختیاری" value={rrn} onChange={(event) => setRrn(event.currentTarget.value)} />
          <Textarea label="توضیحات" minRows={3} value={description} onChange={(event) => setDescription(event.currentTarget.value)} />
          <Group justify="flex-end"><Button variant="default" onClick={modal.close}>انصراف</Button><Button loading={createMutation.isLoading} disabled={!settleDate || !selectedItems.length} onClick={() => createMutation.mutate()}>تأیید و ثبت تسویه</Button></Group>
        </Stack>
      </Modal>

      <Modal
        opened={cancelModalOpened}
        onClose={cancelModal.close}
        title="ابطال سند تسویه"
        centered
      >
        <Stack>
          <Alert color="red" icon={<IconInfoCircle size={18} />}>
            با ابطال سند، آیتم‌های آن دوباره قابل تسویه خواهند شد.
          </Alert>
          <Textarea
            label="دلیل ابطال"
            placeholder="دلیل ابطال سند را وارد کنید"
            minRows={3}
            value={cancelReason}
            onChange={(event) => setCancelReason(event.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={cancelModal.close}>انصراف</Button>
            <Button
              color="red"
              loading={cancelMutation.isLoading}
              disabled={!cancelSettlementId}
              onClick={() =>
                cancelSettlementId &&
                cancelMutation.mutate({ id: cancelSettlementId, reason: cancelReason })
              }
            >
              تأیید ابطال
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={detailModalOpened}
        onClose={detailModal.close}
        title={`جزئیات سند #${detailSettlementId ?? ""}`}
        centered
        size="xl"
      >
        <LoadingOverlay visible={detailQuery.isLoading} />
        {detailQuery.data?.data && (
          <Stack>
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <Paper withBorder p="md"><Text size="xs" c="dimmed">عضو تیم</Text><Text fw={700}>{detailQuery.data.data.merchantUserFullName || "—"}</Text></Paper>
              <Paper withBorder p="md"><Text size="xs" c="dimmed">ثبت‌کننده</Text><Text fw={700}>{detailQuery.data.data.settledByFullName || "—"}</Text></Paper>
              <Paper withBorder p="md"><Text size="xs" c="dimmed">شماره پیگیری</Text><Text fw={700}>{detailQuery.data.data.rrn || "—"}</Text></Paper>
            </SimpleGrid>
            <ScrollArea>
              <Table striped verticalSpacing="sm" miw={700}>
                <Table.Thead><Table.Tr><Table.Th>شناسه خرید</Table.Th><Table.Th>مبلغ ناخالص</Table.Th><Table.Th>کمیسیون</Table.Th><Table.Th>سهم تیم</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {(detailQuery.data.data.items ?? []).map((item) => (
                    <Table.Tr key={item.customerPurchaseItemId}>
                      <Table.Td>{item.customerPurchaseId}</Table.Td>
                      <Table.Td>{money(item.grossAmount ?? 0)}</Table.Td>
                      <Table.Td>{money(item.commissionAmount ?? 0)}</Table.Td>
                      <Table.Td fw={700}>{money(item.amount ?? 0)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Stack>
        )}
      </Modal>
    </Box>
  );
}

export default EmployeeSettlements;
